import * as React from 'react';
import PropTypes from 'prop-types';
import { useTheme } from '@mui/material/styles';
import Box from '@mui/material/Box';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import Chip from '@mui/material/Chip';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import { SparkLineChart } from '@mui/x-charts/SparkLineChart';
import { areaElementClasses } from '@mui/x-charts/LineChart';

function getDaysInMonth(month, year) {
  const date = new Date(year, month, 0);
  const monthName = date.toLocaleDateString('en-US', {
    month: 'short',
  });
  const daysInMonth = date.getDate();
  const days = [];
  let i = 1;
  while (days.length < daysInMonth) {
    days.push(`${monthName} ${i}`);
    i += 1;
  }
  return days;
}

function getLast30Days() {
  const days = []; const currentDate = new Date();
  // Start from 30 days ago and move to the current date 
  for (let i = 29; i >= 0; i--) {
    const date = new Date(); date.setDate(currentDate.getDate() - i);
    const monthName = date.toLocaleDateString('en-US', { month: 'short', });
    const day = date.getDate();
    days.push(`${monthName} ${day}`);
  } return days;
}

function getHoursInDay() {
  const hours = ['1', '2', '1', '2', '1', '2', '1', '2',];
  for (let i = 0; i < 24; i++) {
    let hour = i % 12 === 0 ? 12 : i % 12; // Convert to 12-hour format 
    let period = i < 12 ? 'AM' : 'PM'; // Determine AM or PM 
    hours.push(`${hour} ${period}`);
  }
  return hours;
}

function AreaGradient({ color, id }) {
  return (
    <defs>
      <linearGradient id={id} x1="50%" y1="0%" x2="50%" y2="100%">
        <stop offset="0%" stopColor={color} stopOpacity={0.3} />
        <stop offset="100%" stopColor={color} stopOpacity={0} />
      </linearGradient>
    </defs>
  );
}

AreaGradient.propTypes = {
  color: PropTypes.string.isRequired,
  id: PropTypes.string.isRequired,
};

function calculateTrend(data) {
  const percentageChange = (data[0] === data[data.length - 1]) ? 0 :
    (data[0] === 0 && data[data.length - 1] > 0) ? Infinity :
      Math.round(((data[data.length - 1] - data[0]) / data[0]) * 100);
  const trendValue = (percentageChange === Infinity) ? "∞%" : percentageChange + '%';
  const trend = (Math.abs(percentageChange) < 5) ? "neutral" : (percentageChange > 0) ? "up" : "down";
  return { trendValue, trend };
}

function StatCard({ card: { title, value, interval, data }, timestamps }) {
  const theme = useTheme();
  const daysInWeek = getLast30Days();

  const trendColors = {
    up:
      theme.palette.mode === 'light'
        ? theme.palette.success.main
        : theme.palette.success.dark,
    down:
      theme.palette.mode === 'light'
        ? theme.palette.error.main
        : theme.palette.error.dark,
    neutral:
      theme.palette.mode === 'light'
        ? theme.palette.grey[400]
        : theme.palette.grey[700],
  };

  const labelColors = {
    up: 'success',
    down: 'error',
    neutral: 'default',
  };
  const { trendValue, trend } = calculateTrend(data);
  const color = labelColors[trend];
  const chartColor = trendColors[trend];
  let xAxis;
  if (timestamps == undefined) {
    xAxis = {
      scaleType: 'band',
      data: daysInWeek,
    };
  } else {
    xAxis = {
      scaleType: 'time', data: timestamps.map(d => new Date(d))
      // scaleType: 'time', data: (timestamps.map(d => new Date(d)).sort((a, b) => a - b))
    };
  }

  return (
    <Card variant="outlined" sx={{ height: '100%', flexGrow: 1 }}>
      <CardContent>
        <Typography component="h2" variant="subtitle2" gutterBottom>
          {title}
        </Typography>
        <Stack
          direction="column"
          sx={{ justifyContent: 'space-between', flexGrow: '1', gap: 1 }}
        >
          <Stack sx={{ justifyContent: 'space-between' }}>
            <Stack
              direction="row"
              sx={{ justifyContent: 'space-between', alignItems: 'center' }}
            >
              <Typography variant="h4" component="p">
                {value}
              </Typography>
              <Chip size="small" color={color} label={trendValue} />
            </Stack>
            <Typography variant="caption" sx={{ color: 'text.secondary' }}>
              {interval}
            </Typography>
          </Stack>
          <Box sx={{ width: '100%', height: 50 }}>
            <SparkLineChart
              colors={[chartColor]}
              data={data}
              area
              showHighlight
              showTooltip
              xAxis={xAxis}
              sx={{
                [`& .${areaElementClasses.root}`]: {
                  fill: `url(#area-gradient-${value})`,
                },
              }}
            >
              <AreaGradient color={chartColor} id={`area-gradient-${value}`} />
            </SparkLineChart>
          </Box>
        </Stack>
      </CardContent>
    </Card >
  );
}

StatCard.propTypes = {
  card: PropTypes.shape({
    data: PropTypes.arrayOf(PropTypes.number).isRequired,
    interval: PropTypes.string.isRequired,
    title: PropTypes.string.isRequired,
    value: PropTypes.string.isRequired,
  }).isRequired,
  timestamps: PropTypes.arrayOf(PropTypes.string),
};

export default React.memo(StatCard);
